import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Role = 'OWNER' | 'ADMIN' | 'EDITOR' | 'AUTHOR' | 'VIEWER';

export interface User {
  id: string; email: string; fullName: string; avatarUrl: string | null;
  locale: string; isSuperAdmin: boolean; emailVerified: boolean; createdAt: string;
}

export interface TenantSummary {
  id: string; slug: string; companyName: string; logoUrl: string | null;
  planCode: string; role: Role; status: string;
}

export interface Session {
  accessToken: string;
  expiresIn: number;
  user: User;
  tenants: TenantSummary[];
}

interface AuthState {
  accessToken: string | null;
  user: User | null;
  tenants: TenantSummary[];
  activeTenantId: string | null;
  ready: boolean;
  setSession: (s: Session) => void;
  setActiveTenant: (id: string) => void;
  setUser: (u: User) => void;
  setReady: (r: boolean) => void;
  clear: () => void;
}

/**
 * access token อยู่ในหน่วยความจำ ไม่ลง localStorage
 *
 * เก็บเฉพาะ activeTenantId (ข้อมูลไม่อ่อนไหว) ไว้ให้จำได้ว่าเปิดบริษัทไหนอยู่
 * ส่วน session จริงอยู่ที่ refresh token ใน httpOnly cookie ซึ่ง JS อ่านไม่ได้ —
 * ถ้ามี XSS หลุดเข้ามาได้ ก็ขโมย session ระยะยาวไปไม่ได้
 */
export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      tenants: [],
      activeTenantId: null,
      ready: false,

      setSession: (s) => {
        const current = get().activeTenantId;
        const stillMember = s.tenants.some((t) => t.id === current);
        set({
          accessToken: s.accessToken,
          user: s.user,
          tenants: s.tenants,
          activeTenantId: stillMember ? current : (s.tenants[0]?.id ?? null),
          ready: true,
        });
      },

      setActiveTenant: (id) => set({ activeTenantId: id }),
      setUser: (user) => set({ user }),
      setReady: (ready) => set({ ready }),
      clear: () => set({ accessToken: null, user: null, tenants: [], ready: true }),
    }),
    {
      name: 'blogaris.session',
      partialize: (s) => ({ activeTenantId: s.activeTenantId }),
    },
  ),
);

export function activeTenant(): TenantSummary | null {
  const { tenants, activeTenantId } = useAuth.getState();
  return tenants.find((t) => t.id === activeTenantId) ?? tenants[0] ?? null;
}

const RANK: Record<Role, number> = { VIEWER: 1, AUTHOR: 2, EDITOR: 3, ADMIN: 4, OWNER: 5 };

export function useRole(): Role | null {
  const tenants = useAuth((s) => s.tenants);
  const id = useAuth((s) => s.activeTenantId);
  return tenants.find((t) => t.id === id)?.role ?? null;
}

export function hasRole(role: Role | null, minimum: Role): boolean {
  if (!role) return false;
  return RANK[role] >= RANK[minimum];
}
